import { VenueTicketEvent } from "@greeter/core";
import { DatePeriod } from "@greeter/date";
import { IVenueApi } from "@greeter/venue-api";
import {
  createFetchMachine,
  FetchActorFields,
  unwrapData,
} from "@greeter/xstate-util";
import { addDays, subDays } from "date-fns";
import { assign, raise, setup } from "xstate";

export type TicketEventOverviewMachineApiRequirements = Pick<
  IVenueApi,
  "fetchTicketEvents"
>;

type Filter = "future" | "previous";

export type TicketEventOverviewContext = {
  api: FetchActorFields<TicketEventOverviewMachineApiRequirements>;
  period: DatePeriod;
  ticketEvents?: Array<VenueTicketEvent>;
  filter: Filter;
};

export type TicketEventOverviewEvents =
  | {
      type: "set.ticketEvents";
      data: Array<VenueTicketEvent>;
    }
  | { type: "set.filter"; data: Filter }
  | { type: "fetchTicketEvents" };

export const TicketEventOverviewMachine = setup({
  types: {} as {
    input: {
      api: TicketEventOverviewMachineApiRequirements;
    };
    context: TicketEventOverviewContext;
    events: TicketEventOverviewEvents;
  },
}).createMachine({
  context({ input, spawn, self }) {
    const period = new DatePeriod({
      from: new Date(),
      to: addDays(new Date(), 100),
    });
    const fetchTicketEventsRef = spawn(
      createFetchMachine(input.api.fetchTicketEvents.bind(input.api)),
      { syncSnapshot: true }
    );
    fetchTicketEventsRef.send({ type: "fetch", data: [{ period: period }] });
    fetchTicketEventsRef.on("fetched", (result) =>
      self.send({ type: "set.ticketEvents", data: result.data })
    );

    return {
      api: {
        fetchTicketEventsRef,
      },
      period,
      filter: "future",
    };
  },

  on: {
    "set.ticketEvents": {
      actions: assign({ ticketEvents: unwrapData }),
    },
    "set.filter": {
      actions: [
        assign(({ event }) => ({
          period:
            event.data === "previous"
              ? new DatePeriod({
                  from: subDays(new Date(), 100),
                  to: new Date(),
                })
              : new DatePeriod({
                  from: new Date(),
                  to: addDays(new Date(), 100),
                }),
          filter: event.data,
        })),
        raise({ type: "fetchTicketEvents" }),
      ],
    },
    fetchTicketEvents: {
      actions: ({
        context: {
          period,
          api: { fetchTicketEventsRef },
        },
      }) => fetchTicketEventsRef.send({ type: "fetch", data: [{ period }] }),
    },
  },
});
